/**
 * Admin-facing copy for the office promo-code refusals (GOTU-9821).
 *
 * campaign-service returns a machine-readable `reason` next to a `message`
 * written for engineers: "opt-in refused (promotion_ended): campaign "…" is
 * ended and its window is [2026-01-01T00:00:00Z, …)". We branch on `reason`
 * and never show the raw `message`, the same rule dental-portal's
 * AddDiscountCodeDialog follows for the office's own apply (GOTU-7283). The
 * copy here speaks about "this office" rather than to it.
 *
 * An unknown or missing reason — `invalid_request`, `internal_error`, a 5xx, a
 * dropped connection — falls through to the caller's generic line.
 */

export const APPLY_PROMO_CODE_FALLBACK = 'Could not apply the promo code. Please try again.';
export const REMOVE_PROMO_CODE_FALLBACK = 'Could not remove the promo code. Please try again.';

const APPLY_REFUSALS = {
  invalid_code: "We don't recognize that code. Check the spelling and try again.",
  promotion_ended: "That promotion has ended, so its code can't be applied.",
  not_eligible: "This office isn't eligible for that code. It falls outside the promotion's targeting.",
  already_applied: 'This office already has that code.',
  // Removed by campaign-service #154 (GOTU-8101), but staging and production
  // still send it until that release reaches them.
  already_active_promotion: 'This office already has another promo code running.',
  not_authorized: "Your account doesn't have permission to apply promo codes.",
};

const REMOVE_REFUSALS = {
  not_authorized: "Your account doesn't have permission to remove promo codes.",
};

const reasonOf = (error) => error?.response?.data?.reason;

// `Object.hasOwn` so a reason like `constructor` cannot resolve to a prototype
// member instead of the fallback.
const copyFor = (refusals, error, fallback) => {
  const reason = reasonOf(error);
  return Object.hasOwn(refusals, reason) ? refusals[reason] : fallback;
};

/** What to show when applying a promo code to an office fails. */
export const applyPromoCodeErrorMessage = (error) =>
  copyFor(APPLY_REFUSALS, error, APPLY_PROMO_CODE_FALLBACK);

/** What to show when removing a promo code from an office fails. */
export const removePromoCodeErrorMessage = (error) =>
  copyFor(REMOVE_REFUSALS, error, REMOVE_PROMO_CODE_FALLBACK);
